import { forwardRef } from "react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

export const NativeSelect = forwardRef(({ className, children, ...props }, ref) => (
  <select
    ref={ref}
    className={cn(
      "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
      className,
    )}
    {...props}
  >
    {children}
  </select>
));
NativeSelect.displayName = "NativeSelect";

export function Field({ label, hint, children, className, required }) {
  return (
    <label className={cn("block space-y-1.5", className)}>
      <span className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

export function Panel({ title, icon: Icon, actions, children, className, bodyClassName }) {
  return (
    <section className={cn("rounded-xl border border-border bg-card", className)}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-border">
          <h2 className="font-heading font-semibold flex items-center gap-2 min-w-0">
            {Icon && <Icon size={17} className="text-muted-foreground shrink-0" />}
            <span className="truncate">{title}</span>
          </h2>
          <div className="flex items-center gap-2 shrink-0">{actions}</div>
        </div>
      )}
      <div className={cn("p-4 sm:p-5", bodyClassName)}>{children}</div>
    </section>
  );
}

export function ProgressBar({ percent = 0, className, tone = "primary" }) {
  const color = tone === "success" ? "bg-emerald-500" : "bg-primary";
  return (
    <div className={cn("h-2 w-full rounded-full bg-muted overflow-hidden", className)}>
      <div className={cn("h-full rounded-full transition-all duration-500", color)} style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="text-center py-12 px-4">
      {Icon && (
        <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          <Icon size={22} className="text-muted-foreground" />
        </div>
      )}
      <p className="font-medium">{title}</p>
      {text && <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export const STATUS_STYLES = {
  passe: { label: "Passé", chip: "bg-muted text-muted-foreground border-border", dot: "bg-muted-foreground/60", block: "bg-muted border-l-muted-foreground/50 text-muted-foreground" },
  aujourdhui: { label: "Aujourd'hui", chip: "bg-primary/10 text-primary border-primary/30", dot: "bg-primary", block: "bg-primary/10 border-l-primary text-foreground" },
  futur: { label: "À venir", chip: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30", dot: "bg-emerald-500", block: "bg-emerald-500/10 border-l-emerald-500 text-foreground" },
};

export function StatusChip({ statut }) {
  const s = STATUS_STYLES[statut] || STATUS_STYLES.futur;
  return <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium", s.chip)}><span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />{s.label}</span>;
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel = "Confirmer", destructive = true, onConfirm, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button variant={destructive ? "destructive" : "default"} onClick={onConfirm} disabled={loading}>{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Stat({ label, value, icon: Icon, hint }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {Icon && <Icon size={16} className="text-muted-foreground" />}
      </div>
      <p className="mt-2 font-heading text-3xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}
